import { gql } from '@apollo/client';

import {
  MARKETING_SEO_KEYWORD_ROW_FIELDS,
  MARKETING_SEO_OPPORTUNITY_FIELDS,
} from '../fragments/marketingSeo';

/**
 * ТЗ 0103, этап 4 — чтение SEO (DataForSEO). Доступ — Admin и Marketing;
 * без права доменный хук запрос не шлёт.
 *
 * Имена операций НЕ совпадают с полями бэкенда (`marketingSeoGaps` →
 * `MarketingSeoIdeasList` и т. д.) — правило кодгена.
 *
 * Неделя снимка — ISO-неделя (`2026-W41`) или день; пусто — последняя
 * (сравнения — предыдущая). Отказы (`bad_week`, `bad_zone`, `not_found`…)
 * — GraphQL-ошибкой с `extensions.code`.
 */

/** Недельные снимки выдачи — список выбора «Снимок от … · сравнить с …». */
export const MarketingSeoWeeksListQuery = gql`
  query MarketingSeoWeeksList($limit: Int) {
    marketingSeoWeeks(limit: $limit) {
      week
      isoWeek
      checkedAt
      keywords
      aiAnswers
      state
      partial
    }
  }
`;

/**
 * SEO › «Обзор»: KPI недели против недели сравнения (только запросы обеих
 * недель), «Мы и конкуренты», крупнейшие движения, идеи недели, свежесть
 * снимка и следующий прогон.
 */
export const MarketingSeoOverviewReportQuery = gql`
  query MarketingSeoOverviewReport(
    $week: String
    $compareWeek: String
    $familySlug: String
    $brand: Boolean
  ) {
    marketingSeoOverview(
      week: $week
      compareWeek: $compareWeek
      familySlug: $familySlug
      brand: $brand
    ) {
      week
      compareWeek
      isoWeek
      compareIsoWeek
      ownDomain
      locationCode
      languageCode
      device
      checkedAt
      nextRunAt
      stale
      keywords
      comparableKeywords
      sov
      sovPrev
      top3
      top3Prev
      top10
      top10Prev
      top20
      top20Prev
      avgPosition
      avgPositionPrev
      estClicks
      estClicksPrev
      gscClicks
      gscClicksPrev
      gscImpressions
      gscImpressionsPrev
      gscFrom
      gscTo
      aiAnswers
      aiMentionShare
      aiMentionSharePrev
      aiCitationShare
      aiSmallSample
      referringDomains
      referringDomainsPrev
      domains {
        domain
        label
        isOwn
        sov
        sovPrev
        top3
        top10
        top10Prev
        avgPosition
        estClicks
      }
      movers {
        keywordId
        keyword
        familySlug
        position
        prevPosition
        delta
        change
        volume
      }
      moveCounts {
        new
        lost
        up
        down
      }
      ideas {
        ...MarketingSeoOpportunityFields
      }
      ctrNote
    }
  }
  ${MARKETING_SEO_OPPORTUNITY_FIELDS}
`;

/**
 * SEO › «Позиции»: страница запросов с числами чипов (зоны, изменения) и
 * распределением своих позиций по неделям.
 */
export const MarketingSeoKeywordsListQuery = gql`
  query MarketingSeoKeywordsList(
    $week: String
    $compareWeek: String
    $familySlug: String
    $brand: Boolean
    $tracked: Boolean
    $change: String
    $zone: String
    $domain: String
    $search: String
    $orderBy: String
    $limit: Int
    $offset: Int
  ) {
    marketingSeoKeywords(
      week: $week
      compareWeek: $compareWeek
      familySlug: $familySlug
      brand: $brand
      tracked: $tracked
      change: $change
      zone: $zone
      domain: $domain
      search: $search
      orderBy: $orderBy
      limit: $limit
      offset: $offset
    ) {
      week
      compareWeek
      totalCount
      rows {
        ...MarketingSeoKeywordRowFields
      }
      zoneCounts {
        value
        count
      }
      changeCounts {
        value
        count
      }
      zonesByWeek {
        week
        isoWeek
        top3
        pos4to10
        pos11to20
        beyond20
      }
    }
  }
  ${MARKETING_SEO_KEYWORD_ROW_FIELDS}
`;

/**
 * Панель запроса (`insp=keyword:<id>`): SERP топ-10 с нашей строкой, линии
 * позиций (мы и лидер), ряд GSC, база Labs, частотность по месяцам и
 * возможности запроса.
 */
export const MarketingSeoKeywordCardQuery = gql`
  query MarketingSeoKeywordCard($id: ID!, $weeks: Int) {
    marketingSeoKeyword(id: $id, weeks: $weeks) {
      keyword {
        ...MarketingSeoKeywordRowFields
      }
      serpWeek
      serp {
        rank
        domain
        url
        title
        isOwn
        isTracked
      }
      features
      hasAiOverview
      aiOverviewRefs
      leaderDomain
      positions {
        week
        isoWeek
        own
        leader
      }
      gsc {
        week
        isoWeek
        clicks
        impressions
        position
      }
      labs {
        domain
        position
        url
        etv
        month
      }
      monthlySearches {
        year
        month
        searchVolume
      }
      opportunities {
        ...MarketingSeoOpportunityFields
      }
    }
  }
  ${MARKETING_SEO_KEYWORD_ROW_FIELDS}
  ${MARKETING_SEO_OPPORTUNITY_FIELDS}
`;

/** «Видимость по неделям»: своя доля и доли доменов, зоны своих позиций. */
export const MarketingSeoSeriesReportQuery = gql`
  query MarketingSeoSeriesReport(
    $familySlug: String
    $brand: Boolean
    $weeks: Int
  ) {
    marketingSeoSeries(familySlug: $familySlug, brand: $brand, weeks: $weeks) {
      week
      isoWeek
      ownSov
      top3
      pos4to10
      pos11to20
      beyond20
      domains {
        domain
        label
        isOwn
        sov
        top3
        top10
        avgPosition
        estClicks
      }
    }
  }
`;

/**
 * SEO › «Конкуренты»: матрица метрик × домены (неделя без бренда, база Labs
 * месяца, ссылки, AI) и «Кто где сильнее» — курс × домен.
 */
export const MarketingSeoCompetitorsReportQuery = gql`
  query MarketingSeoCompetitorsReport($week: String, $familySlug: String) {
    marketingSeoCompetitors(week: $week, familySlug: $familySlug) {
      week
      month
      aioKeywords
      aiAnswers
      domains {
        domain
        label
        isOwn
        sov
        top3
        top10
        top20
        avgPosition
        estClicks
        labsEtv
        labsKeywords
        labsTop3
        labsTop10
        labsZones {
          value
          count
        }
        referringDomains
        backlinks
        backlinksRank
        aiMentionShare
        aiCitationShare
        aioCitationShare
      }
      matrix {
        familySlug
        familyName
        isSold
        domain
        isOwn
        keywords
        top10
        top10Share
        sov
      }
    }
  }
`;

/**
 * SEO › «Что написать»: разрывы месяца (строки страницей), карточки
 * кластеров (все строки фильтра), темы без курса и числа чипов.
 */
export const MarketingSeoIdeasListQuery = gql`
  query MarketingSeoIdeasList(
    $month: String
    $familySlug: String
    $unmapped: Boolean
    $minVolume: Int
    $kind: String
    $includeNavigational: Boolean
    $limit: Int
    $offset: Int
  ) {
    marketingSeoGaps(
      month: $month
      familySlug: $familySlug
      unmapped: $unmapped
      minVolume: $minVolume
      kind: $kind
      includeNavigational: $includeNavigational
      limit: $limit
      offset: $offset
    ) {
      month
      totalCount
      rows {
        keywordId
        keyword
        familySlug
        familyName
        isSold
        searchVolume
        intent
        difficulty
        ownPosition
        ownUrl
        competitors {
          domain
          position
          url
        }
        kind
        isQuestion
        opportunityId
        status
        valuePlnEst
      }
      topics {
        key
        label
        familySlug
        hasCourse
        keywords
        searchVolume
        topKeywords
        competitors {
          domain
          keywords
        }
        opportunityId
        status
        intent
        suggestedAction
        score
        courseSearchVolume
      }
      clusters {
        key
        label
        familySlug
        isSold
        keywords
        searchVolume
        kind
        action
        intent
        suggestedAction
        score
        courseSearchVolume
        ownUrl
        kindCounts {
          value
          count
        }
        topKeywords
        competitors {
          domain
          keywords
        }
        opportunityIds
        planned
        valuePlnEst
      }
      kindCounts {
        value
        count
      }
      hiddenCounts {
        value
        count
      }
    }
  }
`;

/**
 * SEO › «AI-ответы»: доли ответов с упоминанием и цитатой (с `n` и флагом
 * малой выборки), домены, недели, вопросы; AI Overview — из выдачи.
 */
export const MarketingAiVisibilityReportQuery = gql`
  query MarketingAiVisibilityReport(
    $week: String
    $familySlug: String
    $system: String
    $group: String
  ) {
    marketingSeoAi(
      week: $week
      familySlug: $familySlug
      system: $system
      group: $group
    ) {
      week
      compareWeek
      isoWeek
      system
      group
      answers
      answersPrev
      smallSample
      mentionShare
      citationShare
      mentionSharePrev
      citationSharePrev
      domains {
        domain
        label
        isOwn
        mentioned
        cited
        mentionShare
        citationShare
        n
      }
      series {
        week
        isoWeek
        system
        n
        mentionShare
        citationShare
      }
      questions {
        id
        text
        group
        familySlug
        answers
        perSystem {
          system
          answers
          mentioned
          cited
        }
        competitors
        citedUrls
      }
      aioKeywords
      aioWithOverview
      aioShare
      aioOwnCited
      aioOwnCitedShare
      aioDomains {
        domain
        keywords
        share
      }
    }
  }
`;

/** Панель вопроса AI (`insp=aiq:<id>`): ответы недели по системам. */
export const MarketingAiAnswerCardQuery = gql`
  query MarketingAiAnswerCard($questionId: ID!, $week: String) {
    marketingSeoAiAnswers(questionId: $questionId, week: $week) {
      id
      system
      week
      repeatNo
      model
      answerText
      sources
      fanOut
      mentions {
        domain
        mentioned
        cited
        firstPos
      }
      ownMentioned
      ownCited
      checkedAt
    }
  }
`;

/**
 * SEO › «Ссылки»: ссылающиеся домены по доменам (месяц и прошлый), новые и
 * потерянные у нас, «ссылаются на конкурентов, но не на нас».
 */
export const MarketingSeoLinksReportQuery = gql`
  query MarketingSeoLinksReport($month: String, $limit: Int) {
    marketingSeoBacklinks(month: $month, limit: $limit) {
      month
      prevMonth
      ownNew
      ownLost
      gapTotal
      domains {
        domain
        label
        isOwn
        referringDomains
        referringDomainsPrev
        referringMainDomains
        backlinks
        rank
      }
      gaps {
        refDomain
        rank
        backlinks
        linksTo
        opportunityId
        status
      }
    }
  }
`;

/**
 * SEO › «Спрос» › «От поиска до продажи»: по курсу — доля видимости, трафик
 * Labs, клики GSC небрендовых запросов, входы из органики (свой счётчик),
 * CRM-лиды и продажи канала seo_google. `null` — источник не собран (≠ 0).
 */
export const MarketingSeoFunnelReportQuery = gql`
  query MarketingSeoFunnelReport($filter: MarketingFilter!) {
    marketingSeoFunnel(filter: $filter) {
      familySlug
      familyName
      isSold
      sov
      labsEtv
      searchVolume
      gscClicks
      gscImpressions
      organicEntrances
      crmLeads
      sales
      revenuePln
    }
  }
`;

/**
 * «Данные» › бюджет DataForSEO: месяц, потолок, потрачено, резерв, баланс,
 * сметы прогонов и последние прогоны. Без кеша сервера.
 */
export const MarketingSeoBudgetCardQuery = gql`
  query MarketingSeoBudgetCard {
    marketingSeoBudget {
      month
      capUsd
      envCapUsd
      spentUsd
      reservedUsd
      uncertainUsd
      remainingUsd
      maxRunUsd
      balanceUsd
      balanceAt
      pricesSource
      pricesVerifiedOn
      enabled
      aiEnabled
      offReason
      aiOffReason
      mode
      estimates {
        kind
        estimateUsd
        keywords
        questions
        ai
        upperBound
        refusal
        items {
          endpoint
          requests
          results
          costUsd
        }
      }
      runs {
        id
        kind
        state
        mode
        week
        month
        ai
        estimateUsd
        spentUsd
        createdAt
        finishedAt
        refusal
        error
      }
      history {
        month
        spentUsd
        reservedUsd
        uncertainUsd
        capUsd
      }
    }
  }
`;

/** Возможности SEO (открытые и в плане по умолчанию) — «Сегодня», ссылки. */
export const MarketingSeoOpportunitiesListQuery = gql`
  query MarketingSeoOpportunitiesList(
    $kinds: [String!]
    $status: [String!]
    $familySlug: String
    $limit: Int
  ) {
    marketingSeoOpportunities(
      kinds: $kinds
      status: $status
      familySlug: $familySlug
      limit: $limit
    ) {
      ...MarketingSeoOpportunityFields
    }
  }
  ${MARKETING_SEO_OPPORTUNITY_FIELDS}
`;

/**
 * Справочник SEO («Настройки → Маркетинг» › SEO): домены (свой,
 * конкуренты, предложенные сервером), вопросы AI по курсам и рынок
 * DataForSEO. Поле то же, что у `MarketingTaxonomyReport`
 * (`marketingTaxonomy`), — объекты справочника кеш сливает
 * (`MarketingTaxonomy: { merge: true }`), и два запроса не затирают друг
 * друга.
 */
export const MarketingSeoTaxonomyReportQuery = gql`
  query MarketingSeoTaxonomyReport {
    marketingTaxonomy {
      seoDomains {
        id
        domain
        label
        isOwn
        active
        source
        brandRegex
        suggestedMetrics
      }
      seoQuestions {
        id
        familySlug
        group
        textPl
        active
        sort
        version
        source
      }
      seoSettings {
        ownDomain
        locationCode
        languageCode
        device
        depth
        maxTracked
        aiRepeats
        minGapVolume
        excludedTopics
        trackedCount
      }
    }
  }
`;
